/**
 * Theme styles for ::render syntax — block/inline pretty widgets,
 * render line backgrounds, and L2 raw mark.
 */

import { EditorView } from "@codemirror/view";
import {
    CM_MONO_FONT_FAMILY,
    accentIconStyles,
    accentText,
    directiveLineStyles,
    directivePrettyStyles,
    statusLineStyles,
} from "../shared/theme-helpers";

const RENDER_ACCENT = "--cm-render-accent";
const RUNNING_ACCENT = "--status-running";
const SUCCESS_ACCENT = "--status-success";
const ERROR_ACCENT = "--status-error";

export const renderTheme = EditorView.baseTheme({
    /* L0 block render — full-line style, same visual weight as define */
    ".cm-magic-render-line": directiveLineStyles(RENDER_ACCENT, 4, 30),
    /* Render line running — green breathing, same as define */
    ".cm-magic-render-line-running": statusLineStyles(RUNNING_ACCENT, {
        backgroundPercent: 5,
        borderPercent: 50,
        animation: "cm-magic-border-breathe 2s ease-in-out infinite alternate",
    }),
    ".cm-magic-render-line-success": statusLineStyles(SUCCESS_ACCENT, {
        backgroundPercent: 6,
        borderPercent: 50,
    }),
    ".cm-magic-render-line-error": statusLineStyles(ERROR_ACCENT, {
        backgroundPercent: 6,
        borderPercent: 50,
    }),
    ".cm-magic-render-pretty-block": directivePrettyStyles(RENDER_ACCENT),
    ".cm-magic-render-pretty-block-arrow": accentIconStyles(RENDER_ACCENT),
    ".cm-magic-render-pretty-block-id": accentText(RENDER_ACCENT, {
        fontWeight: "600",
        letterSpacing: "0.01em",
    }),
    /* L0 inline render — compact inline marker without container fill */
    ".cm-magic-render-pretty-inline": directivePrettyStyles(RENDER_ACCENT, {
        gap: "3px",
        fontSize: "12px",
        cursor: "pointer",
    }),
    ".cm-magic-render-pretty-inline-arrow": accentIconStyles(RENDER_ACCENT),
    ".cm-magic-render-pretty-inline-id": accentText(RENDER_ACCENT, {
        fontWeight: "600",
    }),
    ".cm-magic-render-pretty-plugin": {
        color: "var(--color-muted-foreground)",
        fontFamily: CM_MONO_FONT_FAMILY,
        fontSize: "12px",
        fontWeight: "400",
        lineHeight: "1.2",
        maxWidth: "180px",
        opacity: "0.76",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    ".cm-magic-render-pretty-inline .cm-magic-render-pretty-plugin": {
        maxWidth: "140px",
    },
    ".cm-magic-render-pretty-comment": {
        maxWidth: "260px",
        whiteSpace: "pre-wrap",
        overflow: "visible",
        textOverflow: "clip",
        lineHeight: "1.35",
    },
    /* Render block (L2 — raw syntax shown when cursor on line) */
    ".cm-magic-render": {
        color: "color-mix(in oklab, var(--cm-render-accent) 82%, var(--color-foreground))",
        fontWeight: "500",
        backgroundColor:
            "color-mix(in oklab, var(--cm-render-accent) 8%, transparent)",
        borderRadius: "3px",
        boxShadow:
            "inset 0 0 0 1px color-mix(in oklab, var(--cm-render-accent) 14%, transparent)",
        cursor: "pointer",
        transition: "background-color 0.15s",
        "&:hover": {
            backgroundColor:
                "color-mix(in oklab, var(--cm-render-accent) 18%, transparent)",
        },
    },
    ".cm-magic-inline-render": {
        display: "block",
        margin: "0",
        width: "100%",
        maxWidth: "100%",
        minWidth: "0",
        boxSizing: "border-box",
        backgroundColor:
            "color-mix(in oklab, var(--color-muted-foreground) 4%, var(--color-card))",
        boxShadow:
            "inset 0 1px 0 color-mix(in oklab, var(--color-muted-foreground) 10%, transparent), inset 0 -1px 0 color-mix(in oklab, var(--color-muted-foreground) 10%, transparent)",
        contain: "inline-size",
        overflow: "hidden",
    },
    ".cm-magic-inline-render-content": {
        display: "block",
        padding: "8px 12px",
        color: "var(--color-foreground)",
        width: "100%",
        minWidth: "0",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "auto",
        overflowY: "hidden",
        overscrollBehaviorX: "contain",
    },
    ".cm-magic-inline-render-content .plugin-render-section": {
        margin: "0",
    },
});
